import { isFieldCountableAsQuestion } from '@hub/prose-utils';
import { createSelector } from '@reduxjs/toolkit';
import type { Section } from '@castoredc/castor_prose';
import type { RootState } from 'features/store/store';

export const selectReadyToReceiveSnapshots = (state: RootState) =>
  state.questionnaire.readyToReceiveSnapshots;
export const selectI18nKeys = (state: RootState) =>
  state.questionnaire.i18nKeys;
export const selectFieldsDefinitions = (state: RootState) =>
  state.questionnaire.fieldsDefinitions;
const selectPages = (state: RootState) => state.questionnaire.pages;
export const selectPhase = (state: RootState) => state.questionnaire.phase;
export const selectPageIndex = (state: RootState) =>
  state.questionnaire.pageIndex;
export const selectPresentation = (state: RootState) =>
  state.questionnaire.presentation;
export const selectI18nBaseKey = (state: RootState) =>
  state.questionnaire.i18nBaseKey;
export const selectErrorData = (state: RootState) =>
  state.questionnaire.errorData;
export const selectLoading = (state: RootState) => state.questionnaire.loading;
export const selectResumeInFlight = (state: RootState) =>
  state.questionnaire.resumeInFlight;

export const selectDocData = (state: RootState) => {
  const value = state.questionnaire.docData;
  if (!value) {
    throw new Error(
      '[ASSERT FAILED, REDUX SELECTOR] Attempt to rely on questionnaire.docData while it is not available in store'
    );
  }
  return value;
};

export const selectCurrentSectionName = createSelector(
  selectPages,
  selectPageIndex,
  (pages, index): string | undefined => pages[index]?.name
);

export const selectCurrentSection = createSelector(
  selectPages,
  selectPageIndex,
  (pages, index): Section | undefined => pages[index]
);

const selectPagesCount = createSelector(selectPages, pages => pages.length);

export const selectTotalQuestionsCount = createSelector(
  selectFieldsDefinitions,
  definitions => definitions.filter(d => isFieldCountableAsQuestion(d)).length
);

export const selectQuestionsCountOnPreviousPages = createSelector(
  selectFieldsDefinitions,
  selectPages,
  selectPageIndex,
  (definitions, pages, index) => {
    const previousPages = pages.slice(0, index);
    return previousPages.reduce(
      (sum, page) =>
        sum +
        definitions.filter(
          d => isFieldCountableAsQuestion(d) && page.fields.includes(d.name)
        ).length,
      0
    );
  }
);

export const selectProgress = createSelector(
  selectPhase,
  selectDocData,
  selectPageIndex,
  selectPagesCount,
  selectPresentation,
  (phase, docData, index, count, presentation) =>
    phase === 'postQuestionnaire'
      ? 100
      : count > 0
        ? ((index +
            (presentation?.showSummaryAtEnd
              ? 1 + Number(docData.showSummary)
              : 1)) *
            100) /
          (presentation?.showSummaryAtEnd ? count + 1 : count)
        : 0
);

export const selectPageFieldsDefinitions = createSelector(
  selectFieldsDefinitions,
  selectPages,
  selectPageIndex,
  (definitions, pages, index) => {
    const page = pages[index];
    return page ? definitions.filter(f => page.fields.includes(f.name)) : [];
  }
);

export const selectHasErrorOnCurrentOrPreviousPage = createSelector(
  selectFieldsDefinitions,
  selectPages,
  selectPageIndex,
  (definitions, pages, index) => {
    const fieldsToCheck = pages
      .map(s => s.fields)
      .slice(0, index + 1)
      .flatMap(fieldName => fieldName);

    return !definitions.some(
      d =>
        fieldsToCheck.includes(d.name) &&
        d.kind?.$case === 'inputField' &&
        d.kind.value.validationErrors.length > 0
    );
  }
);

export const selectFirstPageIndexWithError = createSelector(
  selectFieldsDefinitions,
  selectPages,
  (definitions, pages): number | undefined => {
    const index = pages.findIndex(page =>
      definitions.some(
        d =>
          page.fields.includes(d.name) &&
          d.kind?.$case === 'inputField' &&
          d.kind.value.validationErrors.length > 0
      )
    );
    return index === -1 ? undefined : index;
  }
);

export const selectIsPreviousDisabled = createSelector(
  selectDocData,
  selectPageIndex,
  (docData, index) => index === 0 && !docData.showSummary
);

export const selectIsNextPageAvailable = createSelector(
  selectPageIndex,
  selectPagesCount,
  (index, count) => index < count - 1
);

export const selectIsLastPage = createSelector(
  selectPageIndex,
  selectPagesCount,
  (index, count) => index === count - 1
);

export const selectQuestionnaireDatadogRumContext = createSelector(
  selectDocData,
  docData => ({
    documentId: docData.id,
    activityName: docData.activityName,
    name: docData.questionnaireName,
  })
);

export const selectShouldWarnAboutLeaving = (state: RootState): boolean => {
  // It will be annoying to have the dialog shown during development.
  // TODO: [NTH] Consider adding option in dev menu to toggle showing dialog.
  if (window.location.hostname === 'localhost') {
    return false;
  }

  const docData = state.questionnaire.docData;
  if (docData) {
    return docData.status !== 'completed';
  }

  return false;
};
